import { useEffect, useCallback, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, ActionPerformed } from '@capacitor/push-notifications';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const usePushNotifications = () => {
  const [permissionStatus, setPermissionStatus] = useState<'prompt' | 'granted' | 'denied' | 'unknown'>('unknown');
  const [isInitialized, setIsInitialized] = useState(false);

  // Check permission status without requesting
  const checkPermissionStatus = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      return 'unknown';
    }

    try {
      const status = await PushNotifications.checkPermissions();
      setPermissionStatus(status.receive as any);
      return status.receive;
    } catch (error) {
      console.error('Error checking push permission:', error);
      return 'unknown';
    }
  }, []);

  // Initialize listeners only (no permission request)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const initializeListeners = async () => {
      try {
        // Check current permission status
        await checkPermissionStatus();

        // Listen for registration success
        await PushNotifications.addListener('registration', async (token: Token) => {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const { error } = await (supabase as any)
              .from('user_push_tokens')
              .upsert({
                user_id: user.id,
                token: token.value,
                platform: Capacitor.getPlatform(),
                updated_at: new Date().toISOString()
              });
            
            if (error) {
              console.error('Error saving push token:', error);
            }
          }
        });

        // Listen for registration errors
        await PushNotifications.addListener('registrationError', (error: any) => {
          console.error('Push registration error:', error);
        });

        // Listen for notification received while app is in foreground
        await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          toast.info(notification.title || 'New notification', {
            description: notification.body
          });
        });

        // Listen for notification action performed (tap)
        await PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
          const data = action.notification.data;
          if (data?.route) {
            window.location.href = data.route;
          }
        });

        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing push notification listeners:', error);
      }
    };

    initializeListeners();

    return () => {
      if (Capacitor.isNativePlatform()) {
        PushNotifications.removeAllListeners();
      }
    };
  }, [checkPermissionStatus]);

  // Request permission and register - called explicitly by user action
  const requestPermissionAndRegister = useCallback(async (): Promise<boolean> => {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      let permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      setPermissionStatus(permStatus.receive as any);

      if (permStatus.receive !== 'granted') {
        return false;
      }

      // Register with the push notification service
      await PushNotifications.register();
      return true;
    } catch (error) {
      console.error('Error requesting push permissions:', error);
      return false;
    }
  }, []);

  const scheduleLocalNotification = async (title: string, body: string, delay: number) => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    // Note: For more advanced scheduling, consider @capacitor/local-notifications plugin
  };

  return { 
    scheduleLocalNotification,
    requestPermissionAndRegister,
    checkPermissionStatus,
    permissionStatus,
    isInitialized
  };
};
